import { useQueryClient } from '@tanstack/react-query';

import { Segment, SegmentTableItemType } from 'domains/segments/types';
import { Folder } from 'models/types';

import useArchiveSegment from './mutations/useArchiveSegment';
import QueryKeys from './queries/keys';

type Props = {
    activeItem: Segment | Folder;
    refetchTableData: () => void;
    resetActiveItem: () => void;
    setErrorMessage: (error: string) => void;
    setSuccessMessage: (message: string) => void;
};

const useArchiveSegmentItem = ({
    activeItem,
    refetchTableData,
    resetActiveItem,
    setErrorMessage,
    setSuccessMessage,
}: Props) => {
    const queryClient = useQueryClient();

    const onError = (error: Error) => {
        setErrorMessage(error.message);
        resetActiveItem();
    };

    const onSuccess = () => {
        setSuccessMessage(`"${activeItem?.name}" successfully archived.`);
        refetchTableData();
        resetActiveItem();

        // Invalidate segments and archived segments queries to refresh the lists
        queryClient.invalidateQueries(QueryKeys.SEGMENTS);
        queryClient.invalidateQueries(QueryKeys.ARCHIVED_SEGMENTS);
        queryClient.invalidateQueries(QueryKeys.SEGMENT_FOLDERS());
        // Also invalidate all segment queries with different parameters (folderId, type)
        queryClient.invalidateQueries(['segments']);
    };

    const { isLoading, mutate: archiveSegment, isSuccess } = useArchiveSegment({
        onError,
        onSuccess,
    });

    const getArchiveItemFunction = () => {
        if (!activeItem || activeItem.type === SegmentTableItemType.FOLDER) {
            return () => {};
        }

        const archiveData = {
            id: Number(activeItem.id),
        };

        return () => archiveSegment(archiveData);
    };

    return { getArchiveItemFunction, isLoading, isSuccess };
};

export default useArchiveSegmentItem;
